import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../../api/client";
import type { AlertPhoneStatus } from "../../../api/types";
import { Button, Field, TextInput } from "../../../components/ui";

const URL = "/admin/me/alert-phone";

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} data-testid="crew-alerts-toggle"
      className={`relative h-6 w-11 rounded-full border transition-colors disabled:opacity-40 ${on ? "border-signal bg-signal" : "border-white/20 bg-white/5"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function Enroll({ st, onDone }: { st: AlertPhoneStatus; onDone: (s: AlertPhoneStatus) => void }) {
  const [phone, setPhone] = useState("");
  const [mfa, setMfa] = useState("");
  const [sms, setSms] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<AlertPhoneStatus>, msg: string) => {
    setBusy(true);
    try { onDone(await fn()); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!st.pending_masked) return (
    <div className="mt-5 grid max-w-xl gap-4" data-testid="crew-alerts-start-step">
      <Field label="Mobile number" htmlFor="crew-alerts-phone"><TextInput id="crew-alerts-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(702) 555-0100" data-testid="crew-alerts-phone-input" /></Field>
      <Field label="Current 6-digit code or a backup code" htmlFor="crew-alerts-mfa"><TextInput id="crew-alerts-mfa" value={mfa} onChange={(e) => setMfa(e.target.value)} data-testid="crew-alerts-mfa-input" /></Field>
      <div><Button loading={busy} disabled={phone.length < 7 || mfa.length < 6} onClick={() => run(() => api.post(`${URL}/start`, { phone, code: mfa }), "Verification code sent")} data-testid="crew-alerts-send-code">Text me a code</Button></div>
    </div>
  );
  return (
    <div className="mt-5 grid max-w-xl gap-4" data-testid="crew-alerts-verify-step">
      <p className="text-sm text-zinc-300">We texted a code to <span className="font-mono text-white">{st.pending_masked}</span>.</p>
      <Field label="Code from the text" htmlFor="crew-alerts-sms-code"><TextInput id="crew-alerts-sms-code" inputMode="numeric" value={sms} onChange={(e) => setSms(e.target.value)} data-testid="crew-alerts-sms-code-input" /></Field>
      <label className="flex items-start gap-3 text-sm text-zinc-300">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-signal" data-testid="crew-alerts-consent-checkbox" />
        <span data-testid="crew-alerts-consent-text">{st.consent_text}</span>
      </label>
      <div><Button loading={busy} disabled={sms.length < 4 || !consent} onClick={() => run(() => api.post(`${URL}/verify`, { code: sms, consent }), "Alert phone verified")} data-testid="crew-alerts-verify-button">Verify and turn on alerts</Button></div>
    </div>
  );
}

function Enrolled({ st, onDone }: { st: AlertPhoneStatus; onDone: (s: AlertPhoneStatus) => void }) {
  const [mfa, setMfa] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<AlertPhoneStatus>) => {
    setBusy(true);
    try { onDone(await fn()); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 space-y-5">
      <div className="flex flex-wrap items-center gap-4">
        <span className="font-mono text-white" data-testid="crew-alerts-phone">{st.phone_masked}</span>
        <span className="text-[10px] uppercase tracking-wider text-emerald-300">Verified</span>
        <span className="flex items-center gap-2 text-sm text-zinc-300"><Toggle on={st.alerts_enabled} disabled={busy} onChange={(v) => act(() => api.put(`${URL}/alerts`, { enabled: v }))} />Alerts {st.alerts_enabled ? "on" : "off"}</span>
      </div>
      {st.opted_out && <p className="text-xs text-warn" data-testid="crew-alerts-opted-out">This number replied STOP. Text START to it from your phone to receive alerts again.</p>}
      {st.consent_at && <p className="text-xs text-zinc-500" data-testid="crew-alerts-consent-at">Consent recorded {new Date(st.consent_at).toLocaleString()}</p>}
      <div className="flex max-w-xl items-end gap-3">
        <div className="flex-1"><Field label="To change or remove: current 6-digit or backup code" htmlFor="crew-alerts-remove-mfa"><TextInput id="crew-alerts-remove-mfa" value={mfa} onChange={(e) => setMfa(e.target.value)} data-testid="crew-alerts-remove-mfa" /></Field></div>
        <Button variant="secondary" loading={busy} disabled={mfa.length < 6} onClick={() => act(() => api.post(`${URL}/remove`, { code: mfa }))} data-testid="crew-alerts-remove-button">Remove number</Button>
      </div>
    </div>
  );
}

export function CrewAlertsCard() {
  const [st, setSt] = useState<AlertPhoneStatus | null>(null);
  useEffect(() => { api.get<AlertPhoneStatus>(URL).then(setSt).catch((e) => toast.error(errorMessage(e))); }, []);
  return (
    <section className="glass rounded-[20px] p-6" data-testid="security-crew-alerts-card">
      <h2 className="flex items-center gap-2 text-lg font-medium text-white"><BellRing className="h-5 w-5 text-signal" />Crew alert texts</h2>
      <p className="mt-1 text-sm text-zinc-400">Get a text when someone replies LATE or DECLINE. This number is only for alerts. It can't be an employee's clock-in phone.</p>
      {st && !st.feature_enabled && <p className="mt-4 rounded-sm border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-100" data-testid="crew-alerts-feature-off">Alerts are switched off for now. You can still add and verify your number.</p>}
      {st && (st.verified ? <Enrolled st={st} onDone={setSt} /> : <Enroll st={st} onDone={setSt} />)}
    </section>
  );
}
